/**
 * WidgetRenderer — maps one server-driven feed item to its component.
 *
 * The whole point of the server-driven design: this `switch` is the only place
 * that knows the type→component mapping. Unknown types are skipped (return
 * null), so the backend can ship a new widget before the web client supports it.
 *
 * It also owns each widget's "view" impression: every rendered widget is wrapped
 * in an observed element that fires a `view` event (section = the widget's API
 * `type`) the first time it scrolls into view.
 */
import { ReactNode } from 'react'
import { HoroscopeItem } from '../api/types'
import { useImpression } from '../hooks/useImpression'
import { logEvent } from '../lib/analytics'
import { EventAction } from '../lib/eventConstants'
import { BannerImage } from './widgets/BannerImage'
import { TodaysPrediction } from './widgets/TodaysPrediction'
import { AstrologerCard } from './widgets/AstrologerCard'

function renderWidget(item: HoroscopeItem): ReactNode {
  switch (item.type) {
    case 'BANNER_IMAGE':
      return <BannerImage data={item.data} />
    case 'TODAYS_PREDICTION':
      return <TodaysPrediction data={item.data} />
    case 'ASTROLOGER_CARD':
      return <AstrologerCard data={item.data} />
    default:
      // Unknown widget type — ignore it so old clients don't break.
      return null
  }
}

export function WidgetRenderer({ item }: { item: HoroscopeItem }) {
  // section = the widget's API type (BANNER_IMAGE / TODAYS_PREDICTION / …).
  // TODAYS_PREDICTION additionally carries the prediction date — attach it to
  // the view event's meta (serialized as {"date":"…"}) so the impression can be
  // attributed to the day shown. The union narrows item.data on the type check.
  const impressionRef = useImpression<HTMLDivElement>(() => {
    const meta = item.type === 'TODAYS_PREDICTION' ? { date: item.data.date } : undefined
    logEvent({ section: item.type, action: EventAction.VIEW, meta })
  })

  const content = renderWidget(item)
  if (!content) return null

  return <div ref={impressionRef}>{content}</div>
}

export default WidgetRenderer
